/**
 * PasswordChangeCard — dedicated Profile-page card for changing the
 * signed-in user's password.
 *
 * Ships in Phase 1 (2026-07-16). Pre-existing `PUT /auth/profile`
 * supported a bundled password change too, but had a 6-character
 * minimum, no confirm-password field, and hid inside the main
 * "save changes" submit. Users reported not seeing a change-password
 * flow at all — the card was too visually integrated.
 *
 * This component is self-contained:
 *   • Own submit + spinner state (doesn't touch parent's `saving`).
 *   • Own inline validation (min 8, new != current, confirm matches).
 *   • Calls the dedicated `POST /api/user/change-password` endpoint.
 *   • Clears all three inputs on 2xx.
 *   • Shows inline error text plus a toast on any failure so the
 *     specific reason (wrong current pw, min-length, etc.) is
 *     visible even after the toast auto-dismisses.
 */
import React, { useState } from "react";
import { Lock, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const TESTIDS = {
  card:            "profile-password-card",
  currentInput:    "profile-password-current-input",
  newInput:        "profile-password-new-input",
  confirmInput:    "profile-password-confirm-input",
  submitBtn:       "profile-password-update-btn",
  inlineError:     "profile-password-inline-error",
};

// White-input style — matches the "clean/white input" pattern used on
// the Booked-trip detail forms, the Insurance quote form, and the
// Auth page (referenced by the user brief: "white input backgrounds,
// matching the site's clean/white input style used elsewhere").
const INPUT_CLS =
  "w-full px-3 py-2 rounded-lg bg-white border-2 border-tb-cream-3 " +
  "focus:border-tb-gold outline-none font-ui text-tb-navy placeholder:text-tb-navy/40";

export default function PasswordChangeCard() {
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const validate = () => {
    if (!current) return "Enter your current password";
    if (pw.length < 8) return "New password must be at least 8 characters";
    if (pw === current) return "New password must be different from current password";
    if (pw !== confirm) return "New password and confirmation don't match";
    return "";
  };

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const localErr = validate();
    if (localErr) {
      setError(localErr);
      return;
    }
    setError("");
    setBusy(true);
    try {
      await api.post("/user/change-password", {
        current_password: current,
        new_password: pw,
      });
      setCurrent(""); setPw(""); setConfirm("");
      toast.success("Password updated");
    } catch (err) {
      const detail = formatApiErrorDetail(err?.response?.data?.detail)
        || "Couldn't update password";
      setError(detail);
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={TESTIDS.card}
      className="rounded-2xl border-2 border-tb-cream-3 bg-white p-4 space-y-3"
    >
      <div className="font-hand text-tb-navy text-base flex items-center gap-1.5">
        <Lock className="w-4 h-4 text-tb-navy" /> Change Password
      </div>

      <label className="block">
        <span className="font-hand text-tb-navy/70 text-xs">Current password</span>
        <input
          data-testid={TESTIDS.currentInput}
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => { setCurrent(e.target.value); if (error) setError(""); }}
          className={INPUT_CLS}
          disabled={busy}
        />
      </label>

      <label className="block">
        <span className="font-hand text-tb-navy/70 text-xs">New password</span>
        <input
          data-testid={TESTIDS.newInput}
          type="password"
          autoComplete="new-password"
          value={pw}
          onChange={(e) => { setPw(e.target.value); if (error) setError(""); }}
          placeholder="at least 8 characters"
          minLength={8}
          className={INPUT_CLS}
          disabled={busy}
        />
      </label>

      <label className="block">
        <span className="font-hand text-tb-navy/70 text-xs">Confirm new password</span>
        <input
          data-testid={TESTIDS.confirmInput}
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => { setConfirm(e.target.value); if (error) setError(""); }}
          className={INPUT_CLS}
          disabled={busy}
        />
      </label>

      {error && (
        <p
          data-testid={TESTIDS.inlineError}
          role="alert"
          className="font-hand text-tb-coral text-sm"
        >
          {error}
        </p>
      )}

      {/* NOTE: `type="button"` (NOT "submit"). This card is rendered
          inside ProfilePage's outer <form>, and HTML forbids nested
          forms — the browser silently strips the inner one and a
          nested submit button then bubbles up to the outer profile
          form's onSubmit, which doesn't know about passwords. We use
          onClick + preventDefault-free handler instead. */}
      <button
        data-testid={TESTIDS.submitBtn}
        type="button"
        onClick={submit}
        disabled={busy}
        className="w-full py-2.5 rounded-xl bg-tb-navy text-tb-gold-bright font-hand text-base border-2 border-tb-gold tb-tap disabled:opacity-60 flex items-center justify-center gap-2"
      >
        <Save className="w-4 h-4" />
        {busy ? "updating…" : "Update Password"}
      </button>
    </div>
  );
}
